import { Component, Inject, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';

import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiMoleculeBeneficiaryFormComponent } from '../ui-molecule-beneficiary-form/ui-molecule-beneficiary-form.component';
import { createBeneficiaryForm } from '../ui-molecule-beneficiary-form/beneficiary-form.shared';

import {
  BeneficiaryGender,
  BeneficiaryCreate,
  BeneficiaryUpdate,
} from '@core/entities/models/beneficiary.model';

export interface BeneficiaryUpsertData {
  title: string;
  message?: string;
  mode: 'create' | 'edit';
  beneficiary?: any;
  countryId?: string;
  partnerId?: string;
}

export interface BeneficiaryDialogResult {
  mode: 'create' | 'edit';
  payload: BeneficiaryCreate | BeneficiaryUpdate;
  beneficiaryId?: string;
  partnerId?: string;
}

@Component({
  selector: 'ui-molecule-dialog-beneficiary-upsert',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    TranslatePipe,
    UiAtomButtonComponent,
    UiMoleculeBeneficiaryFormComponent,
  ],
  templateUrl: './ui-molecule-dialog-beneficiary-upsert.component.html',
  styleUrl: './ui-molecule-dialog-beneficiary-upsert.component.scss',
})
export class BeneficiaryUpsertDialogComponent implements AfterViewInit {
  form: FormGroup;
  isLoading = false;
  errorMessage = '';
  formSubmitted = false;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    public dialogRef: MatDialogRef<BeneficiaryUpsertDialogComponent, BeneficiaryDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: BeneficiaryUpsertData,
  ) {
    this.form = createBeneficiaryForm(this.fb, this.data.mode);
  }

  ngAfterViewInit(): void {
    // Cargar datos del beneficiario después de que los componentes estén inicializados
    if (this.data.mode === 'edit' && this.data.beneficiary) {
      setTimeout(() => {
        this.loadBeneficiaryData(this.data.beneficiary);
      }, 0);
    }
  }

  private loadBeneficiaryData(beneficiary: any): void {
    const formData = {
      firstName: beneficiary.firstName || '',
      lastName: beneficiary.lastName || '',
      email: beneficiary.email || '',
      phone: beneficiary.phone || '',
      birthDate: beneficiary.birthDate || '',
      gender: this.mapGenderEnumToFormValue(beneficiary.gender || 'PREFER_NOT_TO_SAY'),
      birthCountry: beneficiary.birthCountry || '',
      hostCountry: beneficiary.hostCountry || '',
      externalId: beneficiary.externalId || '',
    };

    this.form.patchValue(formData);

    // Forzar actualización de los componentes de país después de cargar datos
    setTimeout(() => {
      this.form.markAsDirty();
      // Forzar que los country-select actualicen su display
      this.form.get('birthCountry')?.updateValueAndValidity();
      this.form.get('hostCountry')?.updateValueAndValidity();
      this.cdr.detectChanges();
    }, 100);
  }

  private mapGenderEnumToFormValue(gender: BeneficiaryGender): string {
    switch (gender) {
      case 'MALE':
        return 'MALE';
      case 'FEMALE':
        return 'FEMALE';
      case 'NON_BINARY':
        return 'NON_BINARY';
      case 'PREFER_NOT_TO_SAY':
      default:
        return 'PREFER_NOT_TO_SAY';
    }
  }

  private mapGenderValue(value: string): BeneficiaryGender {
    switch (value) {
      case 'MALE':
        return 'MALE';
      case 'FEMALE':
        return 'FEMALE';
      case 'NON_BINARY':
        return 'NON_BINARY';
      case 'PREFER_NOT_TO_SAY':
      default:
        return 'PREFER_NOT_TO_SAY';
    }
  }

  submit(): void {
    this.formSubmitted = true;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    const raw = this.form.getRawValue();
    const mappedGender = raw.gender ? this.mapGenderValue(raw.gender) : undefined;

    if (this.data.mode === 'edit' && this.data.beneficiary) {
      const payload: BeneficiaryUpdate = {
        firstName: raw.firstName,
        lastName: raw.lastName,
        email: raw.email,
        phone: raw.phone,
        birthDate: raw.birthDate || undefined,
        gender: mappedGender,
        birthCountry: raw.birthCountry || undefined,
        hostCountry: raw.hostCountry || undefined,
        externalId: raw.externalId || '',
      };

      this.dialogRef.close({
        mode: 'edit',
        payload: payload,
        beneficiaryId: this.data.beneficiary.id,
        partnerId: this.data.partnerId,
      });
    } else {
      const payload: BeneficiaryCreate = {
        firstName: raw.firstName,
        lastName: raw.lastName,
        email: raw.email,
        phone: raw.phone,
        birthDate: raw.birthDate,
        gender: mappedGender,
        birthCountry: raw.birthCountry,
        hostCountry: raw.hostCountry,
        externalId: raw.externalId || '',
      };

      this.dialogRef.close({
        mode: 'create',
        payload: payload,
        partnerId: this.data.partnerId,
      });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
